/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Root app component with logic for themes and localization.
 *
 * @author Mikhail Kokoulin
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Alexander Golshtein
 * @author Ilya Baranchikov
 * @version 1.25.1
 * @since 1.0.0
 */

import { Fragment, PureComponent } from 'react';

import { Action, Location, UnregisterCallback } from 'history';
import 'reflect-metadata';
import Favicon from 'react-favicon';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import ScrollToTop from '@components/common/ScrollToTop/ScrollToTop';
import Header from '@components/Header/Header';
import { PageContainerStyled } from '@components/PageWrapper/styled/PageWrapperStyled';
import GoogleTagManager from '@modules/analytics/GoogleTagManager/GoogleTagManager';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import ICommonTranslation from '@modules/internationalization/types/common';
import Loader from '@modules/loader/components/Loader';
import ModalWrapper from '@modules/modal/components/ModalWrapper';
import Notification from '@modules/notification/components/Notification';
import { getNotificationsStart, notificationChangeStatus } from '@modules/notification/notificationActions';
import { makeSelectNotificationIsEnabled } from '@modules/notification/notificationSelectors';
import { INotificationChangeStatusPayload } from '@modules/notification/types/actionTypes';
import { ENotificationPlace } from '@modules/notification/types/notificationTypes';
import Onboarding from '@modules/onboarding/components/Onboarding';
import SurveyMedallia from '@modules/surveyMedallia/SurveyMedallia';
import ErrorPage from '@pages/ErrorPage/ErrorPage';
import { IRootReducer } from '@reducers/rootReducer';
import AppRouter from '@routes/AppRouter';
import { RoutePath } from '@routes/constants/routesConstants';
import { icons } from '@theme/index';

import { initAppStart, setAppError } from '../appActions';
import appHistory from '../appHistory';
import { makeSelectAppHasError } from '../appSelectors';
import { ISetAppErrorPayload } from '../appTypes';
import GlobalStyle from './GlobalStyle';
import SupportStyle from './SupportStyle';

interface IDispatchProps {
    initApp: () => void;
    getNotificationsStart: () => void;
    notificationChangeStatus: (payload: INotificationChangeStatusPayload) => void;
    setAppError: (payload: ISetAppErrorPayload) => void;
}

interface IConnectedProps {
    hasError: boolean;
    isNotificationEnable: boolean;
    translation: ICommonTranslation;
}

class App extends PureComponent<IDispatchProps & IConnectedProps> {
    pathListener: UnregisterCallback | undefined;

    componentDidMount(): void {
        this.props.initApp();
        this.onChangePathListener(appHistory.location);
        this.pathListener = appHistory.listen(this.onChangePathListener);
        // Hide default loader
        const loader = document.querySelector('#default-loader');
        if (loader) loader.classList.remove('show');
    }

    /**
     * @description This lifecycle is invoked after an error has been thrown by a descendant component.
     * An object with a componentStack key containing information about which component threw the error.
     */
    componentDidCatch(): void {
        this.props.setAppError({ hasError: true });
    }

    componentWillUnmount(): void {
        this.unmountChangePath();
    }

    onChangePathListener = (location: Location, action?: Action): void => {
        location.pathname === RoutePath.dashboard
            ? this.props.notificationChangeStatus({ isEnable: false, place: ENotificationPlace.global })
            : action !== 'REPLACE' && this.loadNotification();
    };

    loadNotification = (): void => {
        if (!this.props.isNotificationEnable) {
            this.props.notificationChangeStatus({ isEnable: true, place: ENotificationPlace.global });
        }
        this.props.getNotificationsStart();
    };

    unmountChangePath = (): void => {
        if (this.pathListener) {
            this.pathListener();
        }
    };

    render() {
        const translation = this.props.translation;
        return (
            <Loader>
                <PageContainerStyled>
                    <ModalWrapper />
                    <Favicon url={icons.favicon} />
                    <GoogleTagManager />
                    <GlobalStyle />
                    <Notification />
                    <SupportStyle />
                    <ScrollToTop />
                    <SurveyMedallia />
                    {this.props.hasError ? (
                        <Fragment>
                            <Header />
                            <ErrorPage
                                src={icons.errorDeliveryImage3x}
                                text={translation.error.ui.text}
                                title={translation.error.ui.title}
                            />
                        </Fragment>
                    ) : (
                        <AppRouter>
                            <Header />
                            <Onboarding />
                        </AppRouter>
                    )}
                </PageContainerStyled>
            </Loader>
        );
    }
}

const mapConnectToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    hasError: makeSelectAppHasError(),
    isNotificationEnable: makeSelectNotificationIsEnabled(ENotificationPlace.global),
    translation: makeSelectTranslationCommon(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    initApp: () => dispatch(initAppStart()),
    getNotificationsStart: () => dispatch(getNotificationsStart()),
    notificationChangeStatus: (payload: INotificationChangeStatusPayload) =>
        dispatch(notificationChangeStatus(payload)),
    setAppError: (payload: ISetAppErrorPayload) => dispatch(setAppError(payload)),
});

export default connect(mapConnectToProps, mapDispatchToProps)(App);
